import { createElement } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { GlobalSearch } from '@/components/layout/GlobalSearch'
import { LanguageSwitcher } from '@/components/layout/LanguageSwitcher'
import { NotificationBell } from '@/components/layout/NotificationBell'
import { useBreadcrumbLabelValue } from '@/contexts/BreadcrumbContext'
import { useTopBarActionsValue } from '@/contexts/TopBarActionsContext'
import { getTopbarModuleIcon } from '@/components/icons/topbar'

// Maps a top-level route segment to its shell nav translation key.
const SEGMENT_NAV_KEY: Record<string, string> = {
  dashboard: 'dashboard',
  calendar: 'calendar',
  jobs: 'calendar',
  clients: 'clients',
  quotes: 'quotes',
  invoices: 'invoices',
  'credit-notes': 'creditNotes',
  payments: 'payments',
  contracts: 'contracts',
  tasks: 'tasks',
  technicians: 'technicians',
  notifications: 'notifications',
  settings: 'settings',
}

const LIST_PATH_OVERRIDE: Record<string, string> = {
  jobs: '/calendar',
}

const MY_ACCOUNT_PATHS = new Set([
  '/privacy-data',
  '/notifications/preferences',
])

// Sub-segments that are actions/groupers rather than entities — fixed labels.
const ACTION_LABEL_KEY: Record<string, string> = {
  new: 'breadcrumb.actions.new',
  edit: 'breadcrumb.actions.edit',
  report: 'breadcrumb.actions.report',
  preferences: 'breadcrumb.actions.preferences',
}

// Module-specific last-crumb copy (Figma 3397:162352 — Contracts / Create New Contract).
const MODULE_ACTION_LABEL_KEY: Record<string, Record<string, string>> = {
  quotes: {
    new: 'breadcrumb.actions.quotesNew',
    edit: 'breadcrumb.actions.quotesEdit',
  },
  contracts: {
    new: 'breadcrumb.actions.contractsNew',
    edit: 'breadcrumb.actions.contractsEdit',
  },
  invoices: {
    new: 'breadcrumb.actions.invoicesNew',
    edit: 'breadcrumb.actions.invoicesEdit',
  },
}

// Some drawers keep the list route mounted and encode their open state in the
// query string, so creation states need an explicit final breadcrumb crumb.
const QUERY_ACTION_CRUMB: Record<
  string,
  { param: string; value: string; labelKey: string }
> = {
  payments: {
    param: 'panel',
    value: 'create',
    labelKey: 'breadcrumb.actions.paymentsNew',
  },
  tasks: {
    param: 'panel',
    value: 'create',
    labelKey: 'breadcrumb.actions.tasksNew',
  },
  calendar: {
    param: 'create',
    value: '1',
    labelKey: 'breadcrumb.actions.interventionsNew',
  },
}

// Grouping segments with no route of their own (rendered as plain text).
const NON_NAVIGABLE_SEGMENTS = new Set(['report'])

const titleCase = (s: string) =>
  s.charAt(0).toUpperCase() + s.slice(1).replace(/-/g, ' ')

// A path segment is an entity id when it is a uuid or a number.
const isIdSegment = (s: string) =>
  /^[0-9a-fA-F-]{16,}$/.test(s) || /^\d+$/.test(s)

interface Crumb {
  label: string
  to?: string
}

function Breadcrumb() {
  const { t } = useTranslation('shell')
  const { pathname, search } = useLocation()
  const detailLabel = useBreadcrumbLabelValue()

  const parts = pathname.split('/').filter(Boolean)
  const crumbs: Crumb[] = []
  const moduleSeg = parts[0]
  const onDashboard = !moduleSeg || moduleSeg === 'dashboard'
  const onMyAccount = MY_ACCOUNT_PATHS.has(pathname)
  const moduleIcon = createElement(
    getTopbarModuleIcon(
      onMyAccount ? 'my-account' : onDashboard ? 'dashboard' : moduleSeg
    ),
    { className: 'size-3.5' }
  )

  if (onMyAccount) {
    crumbs.push({ label: t('account.myAccount'), to: '/privacy-data' })
  } else if (onDashboard) {
    crumbs.push({ label: t('nav.dashboard'), to: '/dashboard' })
  } else if (moduleSeg) {
    const navKey = SEGMENT_NAV_KEY[moduleSeg]
    crumbs.push({
      label: navKey
        ? t(`nav.${navKey}`)
        : t(`breadcrumb.segments.${moduleSeg}`, {
            defaultValue: titleCase(moduleSeg),
          }),
      to: LIST_PATH_OVERRIDE[moduleSeg] ?? `/${moduleSeg}`,
    })

    let acc = `/${moduleSeg}`
    for (let i = 1; i < parts.length; i++) {
      const seg = parts[i]
      acc += `/${seg}`
      const nextSeg = parts[i + 1]
      if (
        isIdSegment(seg) &&
        nextSeg &&
        MODULE_ACTION_LABEL_KEY[moduleSeg]?.[nextSeg]
      )
        continue

      let label: string
      if (ACTION_LABEL_KEY[seg]) {
        const moduleKey = MODULE_ACTION_LABEL_KEY[moduleSeg]?.[seg]
        label = moduleKey ? t(moduleKey) : t(ACTION_LABEL_KEY[seg])
      } else if (isIdSegment(seg)) label = detailLabel ?? t('breadcrumb.detail')
      else
        label = t(`breadcrumb.segments.${seg}`, {
          defaultValue: titleCase(seg),
        })

      let to: string | undefined
      if (NON_NAVIGABLE_SEGMENTS.has(seg)) to = undefined
      else if (moduleSeg === 'jobs' && isIdSegment(seg))
        to = `/calendar?job=${seg}`
      else to = acc

      crumbs.push({ label, to })
    }

    if (parts.length === 1) {
      const searchParams = new URLSearchParams(search)
      const queryAction = QUERY_ACTION_CRUMB[moduleSeg]
      const editJobId = searchParams.get('edit_job')
      const selectedJobId = searchParams.get('job')

      if (
        queryAction &&
        searchParams.get(queryAction.param) === queryAction.value
      ) {
        crumbs.push({ label: t(queryAction.labelKey) })
      } else if (moduleSeg === 'calendar' && editJobId) {
        crumbs.push({
          label: detailLabel ?? t('breadcrumb.detail'),
          to: `/calendar?job=${encodeURIComponent(editJobId)}`,
        })
        crumbs.push({ label: t('breadcrumb.actions.edit') })
      } else if (moduleSeg === 'calendar' && selectedJobId) {
        crumbs.push({ label: detailLabel ?? t('breadcrumb.detail') })
      } else if (
        moduleSeg === 'payments' &&
        searchParams.get('panel') === 'detail' &&
        searchParams.get('payment')
      ) {
        crumbs.push({ label: detailLabel ?? t('breadcrumb.detail') })
      }
    }
  }

  const moduleRoot = crumbs[0]

  return (
    <nav className='flex min-w-0 items-center gap-2' aria-label="Fil d'Ariane">
      {/* Module badge — icon swaps per page/module (Figma 863:37091). */}
      {moduleRoot?.to ? (
        <Link
          to={moduleRoot.to}
          className='flex size-6 shrink-0 items-center justify-center rounded-lg bg-[#fff1ea] text-[#ff5a1f]'
          aria-label={moduleRoot.label}
        >
          {moduleIcon}
        </Link>
      ) : (
        <span className='flex size-6 shrink-0 items-center justify-center rounded-lg bg-[#fff1ea] text-[#ff5a1f]'>
          {moduleIcon}
        </span>
      )}

      <div className='flex min-w-0 items-center gap-2'>
        {crumbs.map((c, i) => {
          const isLast = i === crumbs.length - 1
          return (
            <span
              key={`${c.label}-${i}`}
              className='flex min-w-0 items-center gap-2'
            >
              {i > 0 && (
                <span
                  className='shrink-0 font-sans text-base font-medium leading-[1.4] text-[#6c7285]'
                  aria-hidden
                >
                  /
                </span>
              )}
              {c.to && !isLast ? (
                <Link
                  to={c.to}
                  className='max-w-48 truncate font-heading text-base font-semibold leading-[1.2] text-[#3d434e] hover:text-[#171a20] hover:underline'
                >
                  {c.label}
                </Link>
              ) : (
                <span
                  className={cn(
                    'max-w-[16rem] truncate font-heading text-base font-semibold leading-[1.2]',
                    isLast ? 'text-[#171a20]' : 'text-[#3d434e]'
                  )}
                  aria-current={isLast ? 'page' : undefined}
                >
                  {c.label}
                </span>
              )}
            </span>
          )
        })}
      </div>
    </nav>
  )
}

export function TopBar() {
  const actions = useTopBarActionsValue()

  return (
    <header className='relative z-50 border-b border-[#e7e9ef] bg-white'>
      <div className='flex items-center justify-between gap-4 px-6 py-4'>
        <Breadcrumb />

        <div className='flex shrink-0 items-center gap-4'>
          <GlobalSearch />
          {actions}
          <NotificationBell />
          <LanguageSwitcher />
        </div>
      </div>
    </header>
  )
}
